import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock } from 'lucide-react';
import { eventsAPI, teamAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { useRegistration } from '../../contexts/RegistrationContext';
import { EventWithHackathon } from '../../types/events';

const EventList: React.FC = () => {
  const [events, setEvents] = useState<EventWithHackathon[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventWithHackathon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  const { user, isLoading: authLoading } = useAuth();
  const { 
    checkRegistration, 
    getRegistrationStatus, 
    isChecking 
  } = useRegistration();

  // Don't render buttons until user context is loaded
  const isUserLoaded = !authLoading;

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (isUserLoaded && user?.role === 'aspirant') {
      fetchUserTeams();
    }
  }, [isUserLoaded, user]);

  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, typeFilter, statusFilter]);

  // Check registration status for all events and hackathons in the list
  useEffect(() => {
    if (isUserLoaded && user && events.length > 0) {
      events.forEach(event => {
        if (event.eventType === 'hackathon' && event.hackathon?._id && !isChecking(event.hackathon._id)) {
          console.log('🔍 EventList: Checking registration for hackathon:', event.hackathon._id);
          checkRegistration(event.hackathon._id, 'hackathon');
        } else if (event.eventType !== 'hackathon' && !isChecking(event._id)) {
          console.log('🔍 EventList: Checking registration for event:', event._id);
          checkRegistration(event._id, 'event');
        }
      });
    }
  }, [events, isUserLoaded, user, checkRegistration, isChecking]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await eventsAPI.getAllEvents();
      setEvents(response);
    } catch (err: any) {
      setError('Failed to fetch events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTeams = async () => {
    try {
      setTeamsLoading(true);
      const response = await teamAPI.getMyTeams();
      setUserTeams(response);
    } catch (err: any) {
      console.error('Error fetching user teams:', err);
      setUserTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const getUserTeamForHackathon = (hackathonId: string) => {
    return userTeams.find(team => team.hackathon?._id === hackathonId);
  };

  const getUserRoleInTeam = (team: any) => {
    if (!team || !user) return null;
    
    // Check if user is leader
    if (team.leader?._id === user._id) {
      return 'leader';
    }
    
    // Check if user is member
    const isMember = team.members?.some((member: any) => member.user?._id === user._id);
    if (isMember) {
      return 'member';
    }
    
    return null;
  };

  const filterEvents = () => {
    let filtered = [...events];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(event => event.eventType === typeFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'upcoming') {
        filtered = filtered.filter(event => new Date(event.startTime) > new Date());
      } else if (statusFilter === 'ongoing') {
        const now = new Date();
        filtered = filtered.filter(event => 
          new Date(event.startTime) <= now && new Date(event.endTime) >= now
        );
      } else if (statusFilter === 'past') {
        filtered = filtered.filter(event => new Date(event.endTime) < new Date());
      }
    }

    setFilteredEvents(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Registration Closed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} days left`;
    return `${hours} hours left`;
  };

  const getEventStatus = (event: EventWithHackathon) => {
    const now = new Date();
    const startDate = new Date(event.startTime);
    const endDate = new Date(event.endTime);
    
    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'ongoing';
    return 'past';
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'hackathon':
        return <Users className="h-4 w-4" />;
      case 'workshop':
        return <Users className="h-4 w-4" />;
      case 'seminar':
        return <Calendar className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show loading spinner if auth is still loading
  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <Button onClick={fetchEvents} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Events</h1>
          </div>
          <Button
            onClick={fetchEvents}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
          >
            <Users className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <p className="text-gray-600">Discover workshops, seminars, hackathons, and more</p>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="hackathon">Hackathons</option>
              <option value="workshop">Workshops</option>
              <option value="seminar">Seminars</option>
              <option value="competition">Competitions</option>
              <option value="other">Other</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="past">Past</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {events.length === 0 ? 'No events available' : 'No events match your filters'}
          </h3>
          <p className="text-gray-600">
            {events.length === 0 
              ? 'Check back later for upcoming events' 
              : 'Try adjusting your search or filters'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <Card key={event._id} className="hover:shadow-lg transition-shadow flex flex-col h-full">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-xl font-semibold text-gray-900 line-clamp-2">
                    {event.title}
                  </CardTitle>
                  <StatusBadge status={getEventStatus(event)} />
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  {getEventIcon(event.eventType)}
                  <span className="capitalize">{event.eventType}</span>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col flex-grow space-y-4">
                <p className="text-gray-600 line-clamp-3">
                  {event.description}
                </p>

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{formatDate(event.startTime)} at {formatTime(event.startTime)} - {formatDate(event.endTime)} at {formatTime(event.endTime)}</span>
                  </div>
                  
                  {event.location && (
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>{event.location}</span>
                    </div>
                  )}
                  
                  {event.eventType === 'hackathon' && event.hackathon ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-2" />
                      <span>{event.hackathon.currentTeams}/{event.hackathon.maxTeams} teams</span>
                    </div>
                  ) : event.maxParticipants && (user?.role === 'admin' || event.showParticipantCount !== false) ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-2" />
                      <span>{event.currentParticipants || 0}/{event.maxParticipants} participants</span>
                    </div>
                  ) : null}
                  
                  {event.registrationDeadline && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-2" />
                      <span>{getTimeRemaining(event.registrationDeadline)}</span>
                    </div>
                  )}
                </div>

                {event.eventType === 'hackathon' && event.hackathon?.prizes && event.hackathon.prizes.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Prizes</h4>
                    <div className="space-y-1">
                      {event.hackathon.prizes.slice(0, 3).map((prize, index) => (
                        <div key={index} className="text-sm text-gray-600">
                          {prize.rank === 1 ? '🥇' : prize.rank === 2 ? '🥈' : '🥉'} {prize.value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-4 mt-auto">
                  <div className="flex gap-2">
                    <Link
                      to={`/events/${event._id}`}
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full">
                        View Details
                      </Button>
                    </Link>
                    
                    {/* Show 3-button layout for non-signed-in users when registration is required */}
                    {event.registrationRequired && isUserLoaded && !user && (
                      <>
                        {event.registrationType === 'optional_signup' ? (
                          <>
                            {/* Existing User button */}
                            <Link
                              to="/login"
                              className="flex-1"
                            >
                              <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                                Existing User
                              </Button>
                            </Link>
                            {/* Guest User button */}
                            <Link
                              to={`/events/${event._id}/register`}
                              className="flex-1"
                            >
                              <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                                Guest User
                              </Button>
                            </Link>
                          </>
                        ) : (
                          <Link
                            to="/login"
                            className="flex-1"
                          >
                            <Button className="w-full">
                              Sign In to Register
                            </Button>
                          </Link>
                        )}
                      </>
                    )}
                    
                    {/* Show registration buttons for signed-in aspirant users */}
                    {event.registrationRequired && isUserLoaded && user?.role === 'aspirant' && (
                      <>
                        {(() => {
                          // For hackathons, check registration status and team membership
                          if (event.eventType === 'hackathon' && event.hackathon) {
                            const registrationStatus = getRegistrationStatus(event.hackathon._id);
                            const isRegistered = registrationStatus?.isRegistered || false;
                            const userTeam = getUserTeamForHackathon(event.hackathon._id);
                            const userRole = getUserRoleInTeam(userTeam);
                            
                            console.log('🎯 EventList Button rendering for hackathon:', event.hackathon._id, {
                              isChecking: isChecking(event.hackathon._id),
                              isRegistered,
                              userRole: user?.role,
                              isUserLoaded,
                              hasTeam: !!userTeam,
                              teamRole: userRole
                            });
                            
                            if (isChecking(event.hackathon._id) || teamsLoading) {
                              return (
                                <div className="flex-1 bg-gray-200 text-gray-600 px-4 py-2 rounded-md text-center text-sm">
                                  Checking...
                                </div>
                              );
                            } else if (!isRegistered) {
                              return (
                                <Link
                                  to={`/events/${event._id}/register`}
                                  className="flex-1"
                                >
                                  <Button className="w-full">
                                    Join Hackathon
                                  </Button>
                                </Link>
                              );
                            } else if (userTeam && userTeam.status && userTeam.status.trim() !== '') {
                              // User is already in a team for this hackathon
                              return (
                                <div className="flex-1 text-sm text-center text-blue-700 bg-blue-50 px-3 py-2 rounded-md">
                                  {userRole === 'leader' ? 'Team Leader' : 'Team Member'}
                                </div>
                              );
                            } else {
                              return (
                                <Link
                                  to={`/teams/create?hackathon=${event.hackathon._id}`}
                                  className="flex-1"
                                >
                                  <Button variant="outline" className="w-full">
                                    Create Team
                                  </Button>
                                </Link>
                              );
                            }
                          } else {
                            // For regular events, check registration status
                            const registrationStatus = getRegistrationStatus(event._id);
                            const isRegistered = registrationStatus?.isRegistered || false;
                            
                            console.log('🎯 EventList Button rendering for regular event:', event._id, {
                              isChecking: isChecking(event._id),
                              isRegistered,
                              userRole: user?.role,
                              isUserLoaded
                            });
                            
                            if (isChecking(event._id)) {
                              return (
                                <div className="flex-1 bg-gray-200 text-gray-600 px-4 py-2 rounded-md text-center text-sm">
                                  Checking...
                                </div>
                              );
                            } else if (!isRegistered) {
                              return (
                                <Link
                                  to={`/events/${event._id}/register`}
                                  className="flex-1"
                                >
                                  <Button className="w-full">
                                    Register Now
                                  </Button>
                                </Link>
                              );
                            } else {
                              // Check if registration requires approval and is still pending
                              const registrationStatus = getRegistrationStatus(event._id);
                              const isPendingApproval = registrationStatus?.requiresApproval && registrationStatus?.status === 'registered';
                              
                              return (
                                <div className={`flex-1 px-4 py-2 rounded-md text-center text-sm ${
                                  isPendingApproval 
                                    ? 'bg-yellow-100 text-yellow-800' 
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {isPendingApproval ? '⏳ Pending' : '✓ Registered'}
                                </div>
                              );
                            }
                          }
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventList; 